import { apiSlice } from '../apiSlice';
import { API_TAGS } from '../../constants/api';
import { MESSAGES } from './notificationsMessages';
import { API_NOTIFICATIONS_URL } from '../../constants/env';

const notificationsSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    fetchNotifications: builder.query({
      query: ({ orgId, urlParams = {} }) => ({
        url: `organizations/${orgId}/notifications`,
        method: 'GET',
        params: {
          ...urlParams
        },
        API_URL: API_NOTIFICATIONS_URL,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_NOTIFICATIONS.ERROR
        }
      }),
      forceRefetch: ({ currentArg, previousArg }) => {
        return currentArg.urlParams.page !== previousArg.urlParams.page;
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}${queryArgs.orgId}`;
      },
      merge: (currentCache, newItems, args) => {
        // handles cases when page needs to be restored to 0
        if (args?.arg?.urlParams?.page === 0) {
          // replace current cache completely
          currentCache.notifications = [...newItems.notifications];
          currentCache.total = newItems.total;
        } else {
          currentCache.notifications.push(...newItems.notifications);
        }
      },
      providesTags: (result, error, args) => {
        return [
          { type: API_TAGS.NOTIFICATIONS, id: 'LIST' }
        ];
      }
    }),
    fetchUnreadNotificationCount: builder.query({
      query: ({ orgId, userId }) => ({
        url: `organizations/${orgId}/notifications/unread-count`,
        method: 'GET',
        params: { userId },
        API_URL: API_NOTIFICATIONS_URL
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}${queryArgs.orgId}`;
      },
      providesTags: [{ type: API_TAGS.NOTIFICATIONS, id: 'LIST' }]
    }),
    readNotification: builder.mutation({
      query: ({ orgId, id, userId, urlParams = {}, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/notifications/read/${id}`,
        method: 'PATCH',
        body: {
          userId
        },
        params: {
          ...urlParams
        },
        API_URL: API_NOTIFICATIONS_URL,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.READ_NOTIFICATION.ERROR
        },
        successMessage: {
          showMessage: false,
          messages: MESSAGES.READ_NOTIFICATION.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      // Update the cache when the mutation is successful
      // This approach takes into account the fact that only one notification is updated,
      // so no refetch is needed
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patcResult = dispatch(
          notificationsSlice.util.updateQueryData('fetchNotifications', arg, (draft) => {
            const updatedNotifications = draft.notifications.map(notification => {
              if (notification.id === arg.id) {
                return {
                  ...notification,
                  read: true
                };
              }
              return notification;
            });
            draft.notifications = updatedNotifications;
          })
        );
        const unreadCountPatchResult = dispatch(
          notificationsSlice.util.updateQueryData('fetchUnreadNotificationCount', { orgId: arg.orgId, userId: arg.userId }, (draft) => {
            draft.unreadNotificationCount = draft.unreadNotificationCount > 0 ? draft.unreadNotificationCount - 1 : 0;
          })
        );
        try {
          await queryFulfilled;
        } catch (error) {
          console.error('Failed to update notification:', error);
          patcResult.undo(); // Undo the cache update
          unreadCountPatchResult.undo();
        }
      }
    }),
    readAllNotifications: builder.mutation({
      query: ({ orgId, userId, urlParams = {}, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/notifications/readAll`,
        method: 'PATCH',
        body: {
          userId
        },
        params: {
          ...urlParams
        },
        API_URL: API_NOTIFICATIONS_URL,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.READ_NOTIFICATION.ERROR
        },
        successMessage: {
          showMessage: false,
          messages: MESSAGES.READ_NOTIFICATION.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patcResult = dispatch(
          notificationsSlice.util.updateQueryData('fetchNotifications', arg, (draft) => {
            draft.notifications = draft.notifications.map(notification => ({
              ...notification,
              read: true
            }));
          })
        );
        const unreadCountPatchResult = dispatch(
          notificationsSlice.util.updateQueryData('fetchUnreadNotificationCount', { orgId: arg.orgId, userId: arg.userId }, (draft) => {
            draft.unreadNotificationCount = 0;
          })
        );
        try {
          await queryFulfilled;
        } catch (error) {
          console.error('Failed to update notification:', error);
          patcResult.undo(); // Undo the cache update
          unreadCountPatchResult.undo();
        }
      }
    }),
    deleteNotification: builder.mutation({
      query: ({ orgId, notificationId, urlParams = {}, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/notifications/${notificationId}`,
        method: 'DELETE',
        params: {
          ...urlParams
        },
        API_URL: API_NOTIFICATIONS_URL,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.DELETE_NOTIFICATION.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.DELETE_NOTIFICATION.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      invalidatesTags: (result) => [{ type: API_TAGS.NOTIFICATIONS }]
    })
  })
});

export const {
  useFetchNotificationsQuery,
  useFetchUnreadNotificationCountQuery,
  useReadNotificationMutation,
  useReadAllNotificationsMutation,
  useDeleteNotificationMutation
} = notificationsSlice;

